/* eslint-disable no-param-reassign */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { DiscountType } from 'checkin/entities/discount';
import { PAYMENT_MODE } from 'checkin/shared/consts';

import type { PaymentInitials, PaymentModeType, PaymentModes, PaymentPageError, PreSubmit } from '../types';

const initialState: PaymentInitials = {
    totalPrice: 0,
    cartId: '',
    paymentMethods: {},
    paymentModes: [],
    maxAvailableMiles: 0,
    paymentPageError: null,
    discountAmount: 0,
    successDiscountType: '',
    selectedMode: PAYMENT_MODE.SBP_QR,
    isDisablePaymentButton: false,
    isLoading: false,
    isOpenQRModal: false,
    isFetchingQR: false,
    isPaymentInProgress: false,
    redirectUrl: '',
    paymentUuid: '',
    paymentStatusAttempt: 0,
};

export const paymentCheckInSlice = createSlice({
    name: 'paymentCheckIn',
    initialState,
    reducers: {
        setPreSubmit: (state, { payload }: PayloadAction<PreSubmit>) => {
            state.totalPrice = payload.price;
            state.paymentMethods = payload.paymentMethods;
            state.maxAvailableMiles = payload.maximumAvailableMiles;
        },
        setPaymentModes: (state, { payload }: PayloadAction<PaymentModes[]>) => {
            state.paymentModes = payload;
        },
        setTotalPrice: (state, { payload }: PayloadAction<number>) => {
            state.totalPrice = payload;
        },
        setDiscountAmount: (state, { payload }: PayloadAction<number>) => {
            state.discountAmount = payload;
        },
        setSuccessDiscountType: (state, { payload }: PayloadAction<DiscountType | ''>) => {
            state.successDiscountType = payload;
        },
        setSelectedMode: (state, { payload }: PayloadAction<PaymentModeType>) => {
            state.selectedMode = payload;
        },
        setIsDisablePaymentButton: (state, { payload }: PayloadAction<boolean>) => {
            state.isDisablePaymentButton = payload;
        },
        setPaymentPageError: (state, { payload }: PayloadAction<Partial<PaymentPageError> | null>) => {
            state.paymentPageError = payload;
        },
        setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
            state.isLoading = payload;
        },
        setIsOpenQRModal: (state, { payload }: PayloadAction<boolean>) => {
            state.isOpenQRModal = payload;
        },
        setIsFetchingQR: (state, { payload }: PayloadAction<boolean>) => {
            state.isFetchingQR = payload;
        },
        setIsPaymentInProgress: (state, { payload }: PayloadAction<boolean>) => {
            state.isPaymentInProgress = payload;
        },
        setRedirectUrl: (state, { payload }: PayloadAction<string>) => {
            state.redirectUrl = payload;
        },
        setPaymentUuid: (state, { payload }: PayloadAction<string>) => {
            state.paymentUuid = payload;
        },
        setPaymentStatusAttempt: (state, { payload }: PayloadAction<number>) => {
            state.paymentStatusAttempt = payload;
        },
    },

    selectors: {
        totalPrice: (state) => state.totalPrice,
        paymentMethods: (state) => state.paymentMethods,
        paymentModes: (state) => state.paymentModes,
        maxAvailableMiles: (state) => state.maxAvailableMiles,
        paymentPageError: (state) => state.paymentPageError,
        discountAmount: (state) => state.discountAmount,
        successDiscountType: (state) => state.successDiscountType,
        selectedMode: (state) => state.selectedMode,
        isDisablePaymentButton: (state) => state.isDisablePaymentButton,
        isLoading: (state) => state.isLoading,
        isOpenQRModal: (state) => state.isOpenQRModal,
        isFetchingQR: (state) => state.isFetchingQR,
        isPaymentInProgress: (state) => state.isPaymentInProgress,
        redirectUrl: (state) => state.redirectUrl,
        paymentUuid: (state) => state.paymentUuid,
        paymentStatusAttempt: (state) => state.paymentStatusAttempt,
    },
});

export const {
    setPreSubmit,
    setTotalPrice,
    setDiscountAmount,
    setSuccessDiscountType,
    setSelectedMode,
    setIsDisablePaymentButton,
    setPaymentPageError,
    setIsLoading,
    setPaymentModes,
    setIsOpenQRModal,
    setIsFetchingQR,
    setIsPaymentInProgress,
    setRedirectUrl,
    setPaymentUuid,
    setPaymentStatusAttempt,
} = paymentCheckInSlice.actions;
